import { groupBy, keyBy } from "lodash";
import * as APITypes from "~/API";
import { getEmptyMonthlyStatusReport } from "../monthlyStatus";
import {
  accumulateExpectedRevenue,
  accumulateIncomeRevenue,
  applyTransactionToIncome,
  expectedStayAmountForReportMonth,
  sumIncomeRevenue,
} from "~/utils/finance/revenueStreams";

export type MonthlyStatusPipelineInput = {
  ownerID: string;
  ownerType: APITypes.OwnerType;
  year: number;
  month: number;
  entityType: APITypes.EntityType;
  entityID: string;
};

export type MonthlyFinanceInputs = {
  properties: APITypes.Property[];
  buildings: APITypes.Building[];
  units: APITypes.Unit[];
  transactions: APITypes.Transaction[];
  leases: APITypes.Lease[];
  offers: APITypes.Offer[];
  serviceRequests: APITypes.ServiceRequest[];
  stays?: APITypes.Stay[];
};

export type MonthlyStatusPipelineOutput = {
  overallMonthlyStatus: APITypes.MonthlyStatusReport;
  propertyMonthlyStatuses: APITypes.MonthlyStatusReport[];
};

type ServiceRequestStats = {
  repairExpenses: number;
  renovationExpenses: number;
  total: number;
};

const EMPTY_SERVICE_REQUEST_STATS: ServiceRequestStats = {
  repairExpenses: 0,
  renovationExpenses: 0,
  total: 0,
};

export const getMonthlyStatusEntityKey = (
  entityType: APITypes.EntityType,
  entityID: string
) => `${entityType}:${entityID}`;

const createServiceRequestStatsByEntityKey = (
  serviceRequests: APITypes.ServiceRequest[]
) => {
  const statsByEntityKey: Record<string, ServiceRequestStats> = {};

  for (const serviceRequest of serviceRequests) {
    const entityType = serviceRequest.entityType as APITypes.EntityType;
    const entityKey = getMonthlyStatusEntityKey(
      entityType,
      serviceRequest.entityID
    );

    const currentStats =
      statsByEntityKey[entityKey] ?? EMPTY_SERVICE_REQUEST_STATS;
    const nextStats: ServiceRequestStats = {
      ...currentStats,
      total: currentStats.total + 1,
    };

    if (serviceRequest.status === APITypes.ServiceRequestStatusType.Approved) {
      if (serviceRequest.isUpgrade) {
        nextStats.renovationExpenses += serviceRequest.finalCost ?? 0;
      } else {
        nextStats.repairExpenses += serviceRequest.finalCost ?? 0;
      }
    }

    statsByEntityKey[entityKey] = nextStats;
  }

  return statsByEntityKey;
};

const aggregateTransactionsIntoReport = (
  report: APITypes.MonthlyStatusReport,
  transactions: APITypes.Transaction[]
) => {
  for (const transaction of transactions) {
    applyTransactionToIncome(report.aggregateIncome, transaction);
  }
};

const applyServiceRequestStatsToReport = (
  report: APITypes.MonthlyStatusReport,
  stats: ServiceRequestStats | undefined
) => {
  if (!stats) return;

  report.aggregateIncome.repairExpenses += stats.repairExpenses;
  report.aggregateIncome.renovationExpenses += stats.renovationExpenses;
  report.numServiceRequests += stats.total;
};

const finalizeReportTotals = (report: APITypes.MonthlyStatusReport) => {
  report.aggregateIncome.totalRevenue = sumIncomeRevenue(
    report.aggregateIncome
  );
  report.aggregateIncome.totalExpenses =
    report.aggregateIncome.repairExpenses +
    report.aggregateIncome.renovationExpenses;
  report.aggregateIncome.net =
    report.aggregateIncome.totalRevenue - report.aggregateIncome.totalExpenses;
};

const aggregatePropertyReportIntoOverall = (
  overallReport: APITypes.MonthlyStatusReport,
  propertyReport: APITypes.MonthlyStatusReport
) => {
  overallReport.numOccupied += propertyReport.numOccupied;
  overallReport.numVacant += propertyReport.numVacant;
  overallReport.numRenovation += propertyReport.numRenovation;
  overallReport.numUnits += propertyReport.numUnits;
  overallReport.numBuildings += propertyReport.numBuildings;
  overallReport.numServiceRequests += propertyReport.numServiceRequests;
  accumulateExpectedRevenue(overallReport, propertyReport);
  accumulateIncomeRevenue(
    overallReport.aggregateIncome,
    propertyReport.aggregateIncome
  );
  overallReport.aggregateIncome.repairExpenses +=
    propertyReport.aggregateIncome.repairExpenses;
  overallReport.aggregateIncome.renovationExpenses +=
    propertyReport.aggregateIncome.renovationExpenses;
};

export const buildMonthlyStatusReports = ({
  input,
  financeInputs,
}: {
  input: MonthlyStatusPipelineInput;
  financeInputs: MonthlyFinanceInputs;
}): MonthlyStatusPipelineOutput => {
  const overallMonthlyStatus = getEmptyMonthlyStatusReport(input);
  overallMonthlyStatus.numProperties = financeInputs.properties.length;

  const buildingsByPropertyId = groupBy(financeInputs.buildings, "propertyID");
  const unitsByBuildingId = groupBy(financeInputs.units, "buildingID");
  const transactionsByUnitId = groupBy(financeInputs.transactions, "entityID");
  const leasesByUnitId = groupBy(financeInputs.leases, "entityID");
  const staysByUnitId = groupBy(financeInputs.stays ?? [], "entityID");
  const offersById = keyBy(financeInputs.offers, "id");
  const serviceRequestStatsByEntityKey = createServiceRequestStatsByEntityKey(
    financeInputs.serviceRequests
  );

  const propertyMonthlyStatuses = financeInputs.properties.map((property) => {
    const propertyMonthlyStatus = getEmptyMonthlyStatusReport({
      ownerID: input.ownerID,
      ownerType: input.ownerType,
      month: input.month,
      year: input.year,
      entityID: property.id,
      entityType: APITypes.EntityType.Property,
    });

    const propertyBuildings = buildingsByPropertyId[property.id] ?? [];
    propertyMonthlyStatus.numBuildings = propertyBuildings.length;

    for (const building of propertyBuildings) {
      const buildingUnits = unitsByBuildingId[building.id] ?? [];

      for (const unit of buildingUnits) {
        aggregateTransactionsIntoReport(
          propertyMonthlyStatus,
          transactionsByUnitId[unit.id] ?? []
        );

        const activeLeasesForUnit = leasesByUnitId[unit.id] ?? [];
        if (activeLeasesForUnit.length > 0) {
          const primaryLease = activeLeasesForUnit[0];
          const offer = primaryLease.offerID
            ? offersById[primaryLease.offerID]
            : undefined;

          propertyMonthlyStatus.expectedRent += offer?.rent ?? 0;
          propertyMonthlyStatus.numOccupied += 1;
        } else if (unit.status === APITypes.UnitStatusType.Vacant) {
          propertyMonthlyStatus.numVacant += 1;
        }

        const unitStays = staysByUnitId[unit.id] ?? [];
        for (const stay of unitStays) {
          propertyMonthlyStatus.expectedStayRevenue =
            (propertyMonthlyStatus.expectedStayRevenue ?? 0) +
            expectedStayAmountForReportMonth(stay, input.month, input.year);
        }

        propertyMonthlyStatus.numUnits += 1;
        applyServiceRequestStatsToReport(
          propertyMonthlyStatus,
          serviceRequestStatsByEntityKey[
            getMonthlyStatusEntityKey(APITypes.EntityType.Unit, unit.id)
          ]
        );
      }

      applyServiceRequestStatsToReport(
        propertyMonthlyStatus,
        serviceRequestStatsByEntityKey[
          getMonthlyStatusEntityKey(APITypes.EntityType.Building, building.id)
        ]
      );
    }

    applyServiceRequestStatsToReport(
      propertyMonthlyStatus,
      serviceRequestStatsByEntityKey[
        getMonthlyStatusEntityKey(APITypes.EntityType.Property, property.id)
      ]
    );

    finalizeReportTotals(propertyMonthlyStatus);
    aggregatePropertyReportIntoOverall(
      overallMonthlyStatus,
      propertyMonthlyStatus
    );

    return propertyMonthlyStatus;
  });

  finalizeReportTotals(overallMonthlyStatus);

  return {
    overallMonthlyStatus,
    propertyMonthlyStatuses,
  };
};
